<template>
    <div class="max-w-7xl mx-auto relative h-full">
        <Organism3DStack
            v-for="(eventGroup, index) in dataGroupedByYear"
            ref="stacksRef"
            :key="eventGroup.year"
            :cards="eventGroup.events"
            :year="eventGroup.year"
            :stack-position="eventGroup.position"
            :is-active="index === currentStackIndex"
            :is-last="index === data.length - 1"
            :config="config"
            @next-stack="passToNextStack"
            @previous-stack="passToPreviousStack"
            @current-card-index-change="handleCurrentCardIndexChange"
            @card-click="handleStackClick(index)"
            v-touch:swipe.left="handleLeftSwipe"
            v-touch:swipe.right="handleRightSwipe"
        />

        <div class="absolute bottom-[20%] left-1/2 transform -translate-x-1/2 w-60 md:hidden">
            <VueSlider
                v-if="maxSliderValue > 0"
                :height="30" :dot-size="30" :contained="true"
                :max="maxSliderValue" v-model="sliderValue" silent
            />
        </div>
    </div>
</template>

<script>
import Molecule3DCard from "../molecules/Molecule3DCard.vue";
import Organism3DStack from "./Organism3DStack.vue";
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/antd.css'
import {cloneDeep} from "lodash";

export default {
    name: 'Organism3DEnv',
    components: {Organism3DStack, Molecule3DCard, VueSlider},

    props: {
        data: {
            type: Array
        }
    },

    data(){
        return{
            allStacks: null,
            config: {
                cardDeltaZ: 100,
                visibleStackCard: 10
            },
            sliderValue: 0,
            maxSliderValue: 0,
            currentStackIndex: 0,
            dataGroupedByYear: [],
            lastScrollTime: 0,
        }
    },

    computed: {
        currentStack() {
            return this.allStacks ? this.allStacks[this.currentStackIndex] : null;
        },
    },

    watch: {
        data: {
            deep: true,
            immediate: true,
            handler: function () {
                const cloneData = cloneDeep(this.data)

                // Group and sort the data
                let groupedAndSortedEvents = cloneData.reduce((result, event) => {
                    const eventDate = new Date(event.finishing_date);
                    const year = eventDate.getFullYear();
                    if (!result.some(entry => entry.year === year)) {
                        result.push({ year, events: [] });
                    }
                    const yearEntry = result.find(entry => entry.year === year);
                    yearEntry.events.push(event);

                    return result;
                }, []);

                //Sort by year
                groupedAndSortedEvents = groupedAndSortedEvents.sort((a, b) => b.year - a.year);

                // Calculate positions for each stack
                groupedAndSortedEvents.forEach((groupedEvent, index) => {
                    if (groupedEvent.position === undefined){
                        // Set the initial position based on the currentStack
                        groupedEvent.position= index - this.currentStackIndex
                        // Set card positions within the stack
                        groupedEvent.events.forEach((event, eventIndex) => {
                            event.positionX = 0;
                            event.positionZ = eventIndex * -this.config.cardDeltaZ;
                        });
                    }
                });

                this.dataGroupedByYear = groupedAndSortedEvents;
                this.changeCurrentStack(0);
            }
        },
        dataGroupedByYear: function (){
            this.updateMaxSliderValue();
            this.refreshAllStacks();
        },
        currentStackIndex: function (newVal, oldVal) {
            this.allStacks.forEach(stack => stack.currentCardIndex = 0)

            //Add 1000ms delay for prevent visual effect when stack change
            setTimeout(() => this.resetPreviousStackCards(), 1000);

            // Update all stacks position
            const delta = newVal - oldVal;
            this.dataGroupedByYear.forEach(folder => {
                folder.position -= delta;
            });

            if (((this.dataGroupedByYear.length - 1) - this.currentStackIndex) < 3){
                this.$emit('load-more');
            }

            this.updateMaxSliderValue();
        },
        sliderValue(newVal) {
            this.currentStack.updateCardsPositions(newVal)
        },
    },

    methods: {
        refreshAllStacks() {
            setTimeout(() => {
            this.allStacks = this.$refs['stacksRef'].sort((stackA, stackB) => stackB._props.year - stackA._props.year);
            },200)
        },
        resetPreviousStackCards() {
            this.dataGroupedByYear.forEach(folder => {
                if (folder.position < (this.currentStackIndex-1)){
                    folder.events.forEach((event, index) => {
                        event.positionZ = -this.config.cardDeltaZ * index;
                        event.positionX = 0;
                    });
                }
            });
        },

        handleStackClick(stackIndex){
            this.changeCurrentStack(stackIndex)
        },
        handleLeftSwipe(){
            this.currentStack.incrementCurrentCardIndex()
        },
        handleRightSwipe(){
            this.currentStack.decrementCurrentCardIndex()
        },
        handleCurrentCardIndexChange(cardIndex) {
            this.sliderValue = cardIndex
        },

        updateMaxSliderValue() {
            if (this.currentStackIndex >= 0 && this.currentStackIndex < this.dataGroupedByYear.length) {
                this.maxSliderValue = this.dataGroupedByYear[this.currentStackIndex].events.length - 1;
            } else {
                this.maxSliderValue = 0;
            }
        },

        passToNextStack() {
            this.changeCurrentStack(this.currentStackIndex + 1)
        },
        passToPreviousStack() {
            this.changeCurrentStack(this.currentStackIndex - 1)
        },
        changeCurrentStack: function(index) {
            const minStack = 0;
            const maxStack = this.dataGroupedByYear.length - 1;

            this.currentStackIndex = Math.max(minStack, Math.min(maxStack, index));
        },

        handleMouseScroll(event){
            const currentTime = Date.now();

            // Check if the time elapsed since the last scroll is more than 200ms
            if (currentTime - this.lastScrollTime > 200) {
                this.currentStack?.handleMouseScroll(event);
                this.lastScrollTime = currentTime;
            }
        },
        handleWindowResize(){
            this.allStacks?.forEach(stack => stack.handleWindowResize())
        },
        handleKeyDown(event){
            this.currentStack.handleKeyDown(event)
        }
    },
    created() {
        document.querySelector('body').addEventListener("wheel", this.handleMouseScroll);
        window.addEventListener("resize", this.handleWindowResize);
        document.addEventListener("keydown", this.handleKeyDown);
    },

    mounted() {
        this.updateMaxSliderValue();
        this.refreshAllStacks();
    },

    beforeDestroy() {
        document.querySelector('body').removeEventListener("wheel", this.handleMouseScroll);
        window.removeEventListener("resize", this.handleWindowResize);
        document.removeEventListener("keydown", this.handleKeyDown);
    },
}
</script>
